import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronRight } from "lucide-react";

import CharacterSvgUrl from "../../assets/characters/choose-action/undraw_newsletter-subscriber_plsr.svg";
import { useAuth } from "../../context/auth-context";

type SubItem = {
  label: string;
  codigo?: string;
  children?: SubItem[];
};

type MainItem = {
  label: string;
  codigo?: string;
  subitems: SubItem[];
  themeColor?: string;
};

type Department = {
  id?: number | string | null;
  name: string;
  slug?: string;
  routeKey?: string;
  themeColor?: string;
  items: MainItem[];
};

const MAIN_THAT_GO_TO_FIRST_SUB = new Set(["saidas", "saídas"]);

const removeAccents = (s: string) =>
  s.normalize("NFD").replace(/[\u0300-\u036f]/g, "");

const norm = (s: string) => removeAccents(String(s)).toLowerCase();

const slugify = (s: string) =>
  norm(s)
    .replace(/&/g, "e")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .trim();

const getDeptPermKey = (d: Department) =>
  d.slug ? String(d.slug) : slugify(d.name);

const getDeptRouteKey = (d: Department) =>
  d.routeKey ? String(d.routeKey) : d.slug ? String(d.slug) : slugify(d.name);

const cleanCodigoForRoute = (dept: Department, codigo?: string) => {
  const raw = String(codigo || "").trim();
  if (!raw) return "";

  const deptKey = slugify(getDeptRouteKey(dept));
  const parts = slugify(raw).split("-").filter(Boolean);

  if (!parts.length) return "";
  if (parts[0] === deptKey) parts.shift();
  if (parts[parts.length - 1] === deptKey) parts.pop();

  return parts.join("-");
};

const getMainSlug = (item: MainItem) => slugify(item.label);

const getSubSlug = (dept: Department, sub: SubItem) => {
  const cleaned = cleanCodigoForRoute(dept, sub.codigo);
  return cleaned || slugify(sub.label);
};

const mapSubItemRecursive = (sub: any): SubItem => ({
  label: String(sub?.label || ""),
  codigo: String(sub?.codigo || ""),
  children: Array.isArray(sub?.children)
    ? sub.children.map((child: any) => mapSubItemRecursive(child))
    : [],
});

const mapDepartmentFromApi = (dept: any): Department => ({
  id: dept?.id ?? null,
  name: String(dept?.nome || ""),
  slug: String(dept?.slug || ""),
  routeKey:
    dept?.routeKey !== undefined && dept?.routeKey !== null
      ? String(dept.routeKey)
      : dept?.route_key !== undefined && dept?.route_key !== null
      ? String(dept.route_key)
      : String(dept?.slug || ""),
  themeColor:
    dept?.themeColor !== undefined && dept?.themeColor !== null
      ? String(dept.themeColor)
      : dept?.theme_color !== undefined && dept?.theme_color !== null
      ? String(dept.theme_color)
      : "#6c63ff",
  items: Array.isArray(dept?.modulos)
    ? dept.modulos.map((mod: any) => ({
        label: String(mod?.label || ""),
        codigo: String(mod?.codigo || ""),
        themeColor:
          mod?.themeColor !== undefined && mod?.themeColor !== null
            ? String(mod.themeColor)
            : mod?.theme_color !== undefined && mod?.theme_color !== null
            ? String(mod.theme_color)
            : dept?.themeColor ?? dept?.theme_color ?? "#6c63ff",
        subitems: Array.isArray(mod?.subitems)
          ? mod.subitems.map((sub: any) => mapSubItemRecursive(sub))
          : [],
      }))
    : [],
});

const buildSubPath = (
  dept: Department,
  item: MainItem,
  sub: SubItem,
  ancestry: SubItem[] = []
): string => {
  const baseDeptPath = `/${getDeptRouteKey(dept)}`;
  const baseMainPath = `${baseDeptPath}/${getMainSlug(item)}`;
  const chain = [...ancestry, sub]
    .map((entry) => getSubSlug(dept, entry))
    .filter(Boolean);

  return `${baseMainPath}/${chain.join("/")}`;
};

const getFirstVisibleNestedPath = (
  dept: Department,
  item: MainItem,
  subitems: SubItem[],
  ancestry: SubItem[] = []
): string | null => {
  for (const sub of subitems) {
    const currentPath = buildSubPath(dept, item, sub, ancestry);
    const children = sub.children || [];

    if (children.length > 0) {
      const deeper = getFirstVisibleNestedPath(dept, item, children, [
        ...ancestry,
        sub,
      ]);
      return deeper || currentPath;
    }

    return currentPath;
  }

  return null;
};

const getMainPath = (dept: Department, item: MainItem) => {
  const baseDeptPath = `/${getDeptRouteKey(dept)}`;
  const mainSlug = getMainSlug(item);
  const baseMainPath = `${baseDeptPath}/${mainSlug}`;
  const visibleSubs = item.subitems || [];

  if (
    MAIN_THAT_GO_TO_FIRST_SUB.has(norm(item.label)) &&
    visibleSubs.length > 0
  ) {
    return getFirstVisibleNestedPath(dept, item, visibleSubs) || baseMainPath;
  }

  return baseMainPath;
};

const findDepartmentFromPath = (
  pathname: string,
  departments: Department[]
): Department | null => {
  const parts = pathname.split("/").filter(Boolean);
  if (!parts.length) return null;

  const deptFromPath = parts[0];

  return (
    departments.find(
      (d) =>
        norm(getDeptRouteKey(d)) === norm(deptFromPath) ||
        norm(getDeptPermKey(d)) === norm(deptFromPath)
    ) || null
  );
};

const shouldShowSubitem = (sub: SubItem) => {
  const n = norm(sub.label);

  return (
    n.includes("registrar") ||
    n.includes("adicionar") ||
    n.includes("produtos") ||
    n.includes("entradas") ||
    n.includes("saidas") ||
    n.includes("saídas") ||
    n.includes("standby")
  );
};

const limitFriendlySubitems = (subs: SubItem[]) => {
  const filtered = subs.filter(shouldShowSubitem);
  return filtered.slice(0, 4);
};

const ChooseAction: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { sessao } = useAuth();

  const [svgMarkup, setSvgMarkup] = useState<string>("");
  const [isLeaving, setIsLeaving] = useState(false);

  const departamentosAcessiveis = useMemo<any[]>(() => {
    const value = sessao?.departamentos_acessiveis;
    return Array.isArray(value) ? value : [];
  }, [sessao?.departamentos_acessiveis]);

  const departamentoSessao = String(sessao?.departamento || "");

  const departments = useMemo<Department[]>(() => {
    return departamentosAcessiveis.map((dept: any) =>
      mapDepartmentFromApi(dept)
    );
  }, [departamentosAcessiveis]);

  const currentDepartment = useMemo<Department | null>(() => {
    const byPath = findDepartmentFromPath(location.pathname, departments);
    if (byPath) return byPath;

    if (departamentoSessao) {
      const bySession = departments.find(
        (d) =>
          norm(getDeptRouteKey(d)) === norm(departamentoSessao) ||
          norm(getDeptPermKey(d)) === norm(departamentoSessao) ||
          norm(d.name) === norm(departamentoSessao)
      );

      if (bySession) return bySession;
    }

    return departments[0] || null;
  }, [location.pathname, departments, departamentoSessao]);

  const accentColor = currentDepartment?.themeColor || "#6c63ff";

  useEffect(() => {
    let active = true;

    const loadSvg = async () => {
      try {
        const response = await fetch(CharacterSvgUrl);
        const raw = await response.text();

        const recolored = raw
          .replace(/#6c63ff/gi, accentColor)
          .replace(/#d6d6e3/gi, "rgba(0,0,0,0.08)");

        if (active) {
          setSvgMarkup(recolored);
        }
      } catch {
        if (active) {
          setSvgMarkup("");
        }
      }
    };

    loadSvg();

    return () => {
      active = false;
    };
  }, [accentColor]);

  const goToWithTransition = (path: string) => {
    if (isLeaving) return;
    setIsLeaving(true);

    window.setTimeout(() => {
      navigate(path);
    }, 180);
  };

  if (!sessao) return null;
  if (!currentDepartment) return null;

  return (
    <div
      style={{
        width: "100%",
        minHeight: "calc(100vh - 56px)",
        background: "#ffffff",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: "24px 20px",
        boxSizing: "border-box",
        opacity: isLeaving ? 0 : 1,
        transform: isLeaving ? "translateY(6px)" : "translateY(0)",
        transition: "opacity 180ms ease, transform 180ms ease",
      }}
    >
      <div
        style={{
          width: "100%",
          maxWidth: 1080,
          display: "grid",
          gridTemplateColumns: "84px minmax(0, 1fr)",
          alignItems: "center",
          gap: 300,
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {svgMarkup ? (
            <div
              style={{
                width: "100%",
                maxWidth: 56,
                lineHeight: 0,
                filter: isLeaving ? "blur(1px)" : "none",
                transform: isLeaving ? "scale(0.985)" : "scale(1)",
                transition: "filter 180ms ease, transform 180ms ease",
              }}
              dangerouslySetInnerHTML={{ __html: svgMarkup }}
            />
          ) : (
            <img
              src={CharacterSvgUrl}
              alt="Escolher ação"
              draggable={false}
              style={{
                width: "100%",
                maxWidth: 56,
                height: "auto",
                objectFit: "contain",
                display: "block",
                userSelect: "none",
              }}
            />
          )}
        </div>

        <div
          style={{
            minWidth: 0,
            opacity: isLeaving ? 0.92 : 1,
            transform: isLeaving ? "translateX(4px)" : "translateX(0)",
            transition: "opacity 180ms ease, transform 180ms ease",
          }}
        >
          <div style={{ marginBottom: 18 }}>
            <div
              style={{
                fontSize: 12,
                fontWeight: 700,
                color: accentColor,
                marginBottom: 6,
                letterSpacing: "0.02em",
              }}
            >
              {currentDepartment.name}
            </div>

            <h1
              style={{
                margin: 0,
                fontSize: 23,
                lineHeight: 1.15,
                fontWeight: 700,
                color: "#1F2937",
                fontFamily: "'Poppins', sans-serif",
              }}
            >
              Qual módulo você gostaria de acessar?
            </h1>
          </div>

          <div
            style={{
              display: "grid",
              gap: 10,
            }}
          >
            {currentDepartment.items.map((item, index) => {
              const friendlySubs = limitFriendlySubitems(item.subitems || []);
              const mainPath = getMainPath(currentDepartment, item);
              const itemAccent = item.themeColor || accentColor;

              return (
                <div
                  key={`${item.label}-${index}`}
                  style={{
                    border: `1px solid ${itemAccent}20`,
                    borderRadius: 14,
                    background: "#fff",
                    padding: 14,
                    boxShadow: "0 4px 14px rgba(15,23,42,0.035)",
                    transition:
                      "transform 160ms ease, box-shadow 160ms ease, border-color 160ms ease",
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.transform = "translateY(-1px)";
                    e.currentTarget.style.boxShadow =
                      "0 10px 22px rgba(15,23,42,0.06)";
                    e.currentTarget.style.borderColor = `${itemAccent}42`;
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.transform = "translateY(0)";
                    e.currentTarget.style.boxShadow =
                      "0 4px 14px rgba(15,23,42,0.035)";
                    e.currentTarget.style.borderColor = `${itemAccent}20`;
                  }}
                >
                  <button
                    type="button"
                    onClick={() => goToWithTransition(mainPath)}
                    style={{
                      width: "100%",
                      background: "transparent",
                      border: "none",
                      padding: 0,
                      margin: 0,
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      textAlign: "left",
                    }}
                  >
                    <div style={{ minWidth: 0 }}>
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: 7,
                          marginBottom: 3,
                        }}
                      >
                        <ChevronRight size={14} color={itemAccent} />
                        <span
                          style={{
                            fontSize: 15,
                            fontWeight: 700,
                            color: "#111827",
                            lineHeight: 1.2,
                          }}
                        >
                          {item.label}
                        </span>
                      </div>

                      <div
                        style={{
                          fontSize: 12,
                          color: "#6B7280",
                          paddingLeft: 21,
                          lineHeight: 1.2,
                        }}
                      >
                        Abrir módulo
                      </div>
                    </div>

                    <ArrowRight size={16} color={itemAccent} />
                  </button>

                  {friendlySubs.length > 0 && (
                    <div
                      style={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 8,
                        marginTop: 12,
                        paddingLeft: 21,
                      }}
                    >
                      {friendlySubs.map((sub, subIndex) => (
                        <button
                          key={`${sub.label}-${subIndex}`}
                          type="button"
                          onClick={() =>
                            goToWithTransition(
                              buildSubPath(currentDepartment, item, sub)
                            )
                          }
                          style={{
                            border: `1px solid ${itemAccent}20`,
                            background: `${itemAccent}0D`,
                            color: itemAccent,
                            borderRadius: 999,
                            padding: "7px 11px",
                            fontSize: 11.5,
                            fontWeight: 700,
                            cursor: "pointer",
                            lineHeight: 1.15,
                            transition:
                              "transform 140ms ease, background 140ms ease, border-color 140ms ease",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.transform =
                              "translateY(-1px)";
                            e.currentTarget.style.background = `${itemAccent}16`;
                            e.currentTarget.style.borderColor = `${itemAccent}42`;
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.transform = "translateY(0)";
                            e.currentTarget.style.background = `${itemAccent}0D`;
                            e.currentTarget.style.borderColor = `${itemAccent}20`;
                          }}
                        >
                          {sub.label}
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChooseAction;
